import React, { useId, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Trophy, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { useQuizStore, QuizFlashcard, MCQQuestion } from '../../stores/quizStore';
import { useI18n } from '../../i18n';

export function QuizView() {
  const answerIdPrefix = useId();
  const { t } = useI18n();
  const {
    questions,
    flashcards,
    activeChallengeId,
    hasGenerated,
    currentIndex,
    userAnswers,
    isComplete,
    setCurrentIndex,
    setUserAnswer,
    completeQuiz,
    resetQuiz,
  } = useQuizStore();

  const [isFlipped, setIsFlipped] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  React.useEffect(() => {
    const state = useQuizStore.getState();
    const saved = state.userAnswers[state.currentIndex - state.flashcards.length];
    setSelectedAnswer(saved === undefined ? '' : String(saved));
    setIsFlipped(false);
    setShowExplanation(false);
  }, [activeChallengeId, currentIndex, userAnswers]);

  const allChallenges: Array<{ type: 'flashcard'; data: QuizFlashcard } | { type: 'question'; data: MCQQuestion }> = [
    ...flashcards.map(fc => ({ type: 'flashcard' as const, data: fc })),
    ...questions.map(q => ({ type: 'question' as const, data: q })),
  ];
  
  const totalChallenges = allChallenges.length;
  const currentChallenge = allChallenges[currentIndex];

  // Empty state - no quiz generated yet
  if (!hasGenerated || totalChallenges === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-white to-gray-50 p-8">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 flex items-center justify-center">
            <Sparkles className="h-10 w-10 text-[#7367FF]" />
          </div>
          <h2 className="text-2xl font-display text-[#434343] mb-3">{t('quiz.emptyTitle')}</h2>
          <p
            className="text-[#434343]/60 mb-6"
            // Copy carries a <strong> emphasis, so it is injected rather than escaped.
            dangerouslySetInnerHTML={{ __html: t('quiz.emptyBody') }}
          />
          <div className="space-y-3 text-start bg-white/60 backdrop-blur-xl rounded-2xl p-6 border border-white/40">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#7367FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-[#7367FF]">1</span>
              </div>
              <p className="text-sm text-[#434343]">{t('quiz.emptyStep1')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#7367FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-[#7367FF]">2</span>
              </div>
              <p className="text-sm text-[#434343]">{t('quiz.emptyStep2')}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-[#7367FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-[#7367FF]">3</span>
              </div>
              <p className="text-sm text-[#434343]">{t('quiz.emptyStep3')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleNext = () => {
    // Save answer for questions
    if (currentChallenge?.type === 'question' && selectedAnswer !== '') {
      const questionIdx = currentIndex - flashcards.length;
      setUserAnswer(questionIdx, parseInt(selectedAnswer));
    }

    if (currentIndex < totalChallenges - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
      setShowExplanation(false);
      // Load saved answer for next question if exists
      const nextChallenge = allChallenges[currentIndex + 1];
      if (nextChallenge?.type === 'question') {
        const nextQIdx = currentIndex + 1 - flashcards.length;
        const saved = userAnswers[nextQIdx];
        setSelectedAnswer(saved !== undefined ? saved.toString() : '');
      } else {
        setSelectedAnswer('');
      }
    } else {
      completeQuiz();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
      setShowExplanation(false);
      const prevChallenge = allChallenges[currentIndex - 1];
      if (prevChallenge?.type === 'question') {
        const prevQIdx = currentIndex - 1 - flashcards.length;
        const saved = userAnswers[prevQIdx];
        setSelectedAnswer(saved !== undefined ? saved.toString() : '');
      } else {
        setSelectedAnswer('');
      }
    }
  };

  const handleRestart = () => {
    resetQuiz();
    setIsFlipped(false);
    setShowExplanation(false);
    setSelectedAnswer('');
  };

  // Grading screen
  if (isComplete) {
    const totalQuestions = questions.length;
    const correctAnswers = questions.filter(
      (q, idx) => userAnswers[idx] === q.correctAnswer
    ).length;
    const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 100;
    const scoreColor = score >= 80 ? 'text-green-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';

    return (
      <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-white to-gray-50 p-8 overflow-auto">
        <div className="max-w-2xl w-full bg-white/70 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/40">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 flex items-center justify-center">
              <Trophy className="h-8 w-8 text-[#7367FF]" />
            </div>
            <h2 className="text-2xl font-display text-[#434343] mb-2">{t('quiz.completeTitle')}</h2>
            {totalQuestions > 0 && (
              <>
                <div className={`text-6xl font-display ${scoreColor} mb-2`}>{score}%</div>
                <p className="text-[#434343]/60">
                  {t('quiz.score', { correct: correctAnswers, total: totalQuestions })}
                </p>
              </>
            )}
            {flashcards.length > 0 && (
              <p className="text-[#434343]/60 mt-1">
                {t('quiz.reviewedFlashcards', { count: flashcards.length })}
              </p>
            )}
          </div>

          {/* Question Review */}
          {totalQuestions > 0 && (
            <div className="space-y-3 mb-6">
              <p className="text-sm font-medium text-[#434343]/80">{t('quiz.reviewAnswers')}</p>
              {questions.map((q, idx) => {
                const userAnswer = userAnswers[idx];
                const isCorrect = userAnswer === q.correctAnswer;
                return (
                  <div key={idx} className={`p-4 rounded-xl border ${isCorrect ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                    <div className="flex items-start gap-2">
                      {isCorrect ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p dir="auto" className="text-sm text-[#434343] font-medium mb-1">{q.question}</p>
                        {!isCorrect && (
                          <p className="text-xs text-[#434343]/60">
                            {t('quiz.yourAnswer')} <bdi dir="auto" className="text-red-500">{q.options[userAnswer] || t('quiz.notAnswered')}</bdi>
                            {' | '}
                            {t('quiz.correctAnswer')} <bdi dir="auto" className="text-green-600">{q.options[q.correctAnswer]}</bdi>
                          </p>
                        )}
                        {q.explanation && (
                          <p dir="auto" className="text-xs text-[#434343]/50 mt-1 italic">{q.explanation}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-3">
            <Button
              className="w-full bg-[#7367FF] hover:bg-[#5f56e6] text-white shadow-lg shadow-[#7367FF]/30"
              onClick={handleRestart}
            >
              <RotateCcw className="h-4 w-4 me-2" />
              {t('quiz.retake')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentChallenge) return null;

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-white to-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/20 bg-white/60 backdrop-blur-xl px-6 py-4 shadow-sm">
        <span className="text-sm text-[#434343]">
          {t('quiz.progress', { current: currentIndex + 1, total: totalChallenges })}
        </span>
        <div className="flex gap-1">
          {allChallenges.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex
                  ? 'bg-[#7367FF] w-6'
                  : idx < currentIndex
                  ? 'bg-[#7367FF]/40'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
        {currentChallenge.type === 'flashcard' ? (
          // Flashcard View
          <div
            className="relative w-full max-w-2xl h-96 cursor-pointer perspective-1000"
            onClick={() => setIsFlipped(!isFlipped)}
          >
            <div
              className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${
                isFlipped ? 'rotate-y-180' : ''
              }`}
            >
              {/* Front of card */}
              <div
                className={`absolute w-full h-full bg-white/70 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/40 flex flex-col items-center justify-center backface-hidden ${
                  isFlipped ? 'invisible' : 'visible'
                }`}
              >
                <p className="text-sm text-[#7367FF] mb-4 uppercase tracking-wide font-medium">{t('quiz.flashcard')}</p>
                <p dir="auto" className="text-xl text-[#434343] text-center leading-relaxed">
                  {(currentChallenge.data as QuizFlashcard).front}
                </p>
                <p className="text-xs text-[#434343] opacity-60 mt-8">{t('quiz.clickToFlip')}</p>
              </div>

              {/* Back of card */}
              <div
                className={`absolute w-full h-full bg-[#7367FF]/10 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-[#7367FF]/30 flex flex-col items-center justify-center rotate-y-180 backface-hidden ${
                  isFlipped ? 'visible' : 'invisible'
                }`}
              >
                <p className="text-sm text-[#7367FF] mb-4 uppercase tracking-wide font-medium">{t('quiz.answer')}</p>
                <p dir="auto" className="text-lg text-[#434343] text-center leading-relaxed">
                  {(currentChallenge.data as QuizFlashcard).back}
                </p>
                <p className="text-xs text-[#434343] opacity-60 mt-8">{t('quiz.clickToFlip')}</p>
              </div>
            </div>
          </div>
        ) : (
          // Question View
          <div className="w-full max-w-2xl">
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-white/40">
              <div className="mb-6">
                <p className="text-sm text-[#7367FF] mb-3 uppercase tracking-wide font-medium">{t('quiz.question')}</p>
                <p dir="auto" className="text-xl text-[#434343] leading-relaxed">
                  {(currentChallenge.data as MCQQuestion).question}
                </p>
              </div>

              <RadioGroup value={selectedAnswer} onValueChange={value => {
                setSelectedAnswer(value);
                setUserAnswer(currentIndex - flashcards.length, Number(value));
              }}>
                <div className="space-y-3">
                  {(currentChallenge.data as MCQQuestion).options.map((option, idx) => {
                    const optionId = `${answerIdPrefix}-option-${idx}`;
                    const isAnswered = showExplanation;
                    const isCorrect = idx === (currentChallenge.data as MCQQuestion).correctAnswer;
                    const isSelected = selectedAnswer === idx.toString();
                    
                    let borderColor = 'border-white/40';
                    let bgColor = 'bg-white/60 hover:bg-white/80';
                    
                    if (isAnswered) {
                      if (isCorrect) {
                        borderColor = 'border-green-300';
                        bgColor = 'bg-green-50';
                      } else if (isSelected && !isCorrect) {
                        borderColor = 'border-red-300';
                        bgColor = 'bg-red-50';
                      }
                    }
                    
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 p-4 rounded-lg ${bgColor} border ${borderColor} transition-all cursor-pointer`}
                      >
                        <RadioGroupItem
                          value={idx.toString()}
                          id={optionId}
                          aria-label={option}
                          disabled={showExplanation}
                        />
                        <Label
                          dir="auto"
                          htmlFor={optionId}
                          className="flex-1 text-[#434343] cursor-pointer"
                        >
                          {option}
                        </Label>
                        {isAnswered && isCorrect && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                        {isAnswered && isSelected && !isCorrect && (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>

              {/* Check Answer / Explanation */}
              {!showExplanation && selectedAnswer !== '' && (
                <Button
                  className="mt-4 w-full bg-[#7367FF]/10 hover:bg-[#7367FF]/20 text-[#7367FF]"
                  variant="ghost"
                  onClick={() => setShowExplanation(true)}
                >
                  {t('quiz.checkAnswer')}
                </Button>
              )}

              {showExplanation && (currentChallenge.data as MCQQuestion).explanation && (
                <div className="mt-4 p-4 rounded-lg bg-[#7367FF]/5 border border-[#7367FF]/20">
                  <p className="text-sm text-[#434343]/80">
                    <span className="font-medium text-[#7367FF]">{t('quiz.explanationLabel')}</span>
                    <bdi dir="auto">{(currentChallenge.data as MCQQuestion).explanation}</bdi>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between border-t border-white/20 bg-white/60 backdrop-blur-xl px-6 py-4">
        <Button
          variant="ghost"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="hover:bg-white/60 text-[#434343] disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4 me-2 rtl-flip" />
          {t('common.previous')}
        </Button>

        <Button
          onClick={handleNext}
          className="bg-[#7367FF] hover:bg-[#5f56e6] text-white shadow-lg shadow-[#7367FF]/30"
        >
          {currentIndex === totalChallenges - 1 ? t('common.finish') : t('common.next')}
          <ChevronRight className="h-4 w-4 ms-2 rtl-flip" />
        </Button>
      </div>
    </div>
  );
}
